import { useState } from "react";
import { api } from "@/lib/api";
import { compressImage, humanUploadError } from "@/lib/imageCompress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { HitRing } from "@/components/loading";
import { MultiPhotoInput } from "@/components/MultiPhotoInput";
import { ExistsInCatalog } from "@/components/publish/ExistsInCatalog";
import { CONDITIONS, condLabel } from "@/lib/conditions";
import { SellerNetCards } from "@/components/publish/SellerNetCards";
import { shipRange, hasShipRates } from "@/lib/sellerNet";

const DRAFT_CONDS = CONDITIONS.map((c) => [c.value, condLabel(c.value)]);

const Steps = () => (
  <div className="grid grid-cols-3 gap-2 text-[10px] sm:text-xs" data-testid="p5-steps">
    {[["01", "Llenas y envías"], ["02", "Revisión < 12 h"], ["03", "Se publica sola"]].map(([n, t], i) => (
      <div key={n} className={`p-2 rounded-[12px] border ${i === 0 ? "border-[#FF4438] text-white" : "border-[#262626] text-neutral-400"}`}>
        <div className="font-mono text-[#FF6B5E]">{n}</div>{t}
      </div>
    ))}
  </div>
);

export async function uploadPublic(file) {
  const { file: toUpload } = await compressImage(file);
  const fd = new FormData();
  fd.append("file", toUpload);
  fd.append("scope", "public");
  fd.append("kind", "card");
  const r = await api.post("/files/upload", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 60000,
  });
  return r.data.path;
}

/** Single-item "Registrar carta" form — pide al admin que agregue una
 * carta que no existe en el catálogo. No incluye precio ni cantidad: eso
 * viene después en "Cargar" cuando ya esté aprobada. */
export function CustomCardForm({ onSuccess, onCancel, onPublishExisting, game = "pokemon", initialName = "", shippingConfig, onConfigureShip }) {
  const [existing, setExisting] = useState(null);
  const [form, setForm] = useState({
    name: initialName, set_name: "", serie_name: "", number: "",
    language: "en", variant: "normal", notes: "", product_type: "card",
    pattern_label: "",
  });
  const [photos, setPhotos] = useState([]);
  const [draft, setDraft] = useState({ condition: "NM", price: "", quantity: 1, bulk: false });
  const draftForP3 = () => ({
    form: { price: draft.price, quantity: draft.quantity, language: form.language, bulk_disclaimer_accepted: draft.bulk,
      condition: draft.condition },
    photos,
  });
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const isRiftbound = game === "riftbound";

  // Ejemplos y variantes por juego. Riftbound no tiene "reverse holo" ni
  // "price pack" — usa promo / overnumbered / alterna como sus 4 variantes.
  const placeholders = isRiftbound
    ? { serie: "Ej. Origins (OGN)", number: "Ej. 030/298 o 229★/219" }
    : { serie: "Ej. Escarlata y Púrpura", number: "Ej. 089/198" };
  const variantOptions = isRiftbound
    ? [
        { value: "normal", label: "Normal" },
        { value: "promo", label: "Promo" },
        { value: "overnumbered", label: "Overnumbered (★)" },
        { value: "alt_art", label: "Alterna (Alt-Art)" },
      ]
    : [
        { value: "normal", label: "Normal" },
        { value: "reverse_holo", label: "Retroholográfica" },
        { value: "price_pack", label: "Price Pack" },
        { value: "patron", label: "Patrón" },
      ];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.set_name.trim()) return toast.error("Nombre y set son obligatorios");
    if (!(parseFloat(draft.price) >= 0.25)) return toast.error("Pon tu precio (mínimo $0.25)");
    if (draft.quantity > 1 && !draft.bulk) return toast.error("Confirma que TODAS las unidades están en la misma condición");
    if (photos.length < 2) return toast.error("Sube al menos 2 fotos reales (frente y dorso)");
    if (!hasShipRates(shippingConfig)) return toast.error("Configura tu envío para publicar");
    setBusy(true);
    setProgress("Buscando en el catálogo…");
    try {
      const { data } = await api.post("/custom-cards/check-existing", { game, name: form.name, set_name: form.set_name, number: form.number, product_type: "card" });
      if (data.matches?.length) { setExisting(data.matches); return; }
    } catch { /* si la verificación falla, el backend vuelve a validar al enviar */ }
    finally { setBusy(false); setProgress(""); }
    await sendRequest({});
  };

  const sendRequest = async (extra) => {
    if (photos.length < 2) return toast.error("Sube al menos 2 fotos reales (frente y dorso)");
    setBusy(true);
    setProgress("Comprimiendo fotos…");
    try {
      const uploaded = await Promise.all(
        photos.map(async (f, i) => {
          try { return await uploadPublic(f); }
          catch (err) { throw new Error(`Foto ${i + 1}: ${humanUploadError(err)}`); }
        }),
      );
      setProgress("Enviando al admin…");
      await api.post("/custom-cards", { ...form, ...extra, photo_paths: uploaded, game,
        pub_draft: { condition: draft.condition, price: parseFloat(draft.price), quantity: draft.quantity, language: form.language,
          bulk_disclaimer_accepted: draft.quantity > 1 && draft.bulk, ship_cost: shipRange(shippingConfig)[0] } });
      setDraft({ condition: "NM", price: "", quantity: 1, bulk: false });
      setExisting(null);
      setForm({ name: "", set_name: "", serie_name: "", number: "", language: "en", variant: "normal", notes: "", product_type: "card", pattern_label: "" });
      setPhotos([]);
      onSuccess && onSuccess();
    } catch (err) {
      const det = err?.response?.data?.detail;
      if (det?.code === "exists_in_catalog") { setExisting(det.matches || []); return; }
      const isAxios = err && (err.response || err.request || err.isAxiosError);
      const msg = (!isAxios && err instanceof Error && err.message) ? err.message : humanUploadError(err);
      toast.error(msg);
    } finally {
      setBusy(false);
      setProgress("");
    }
  };

  if (existing?.length) {
    return <ExistsInCatalog matches={existing} onPublish={(m) => onPublishExisting && onPublishExisting(m, draftForP3())} onBack={() => setExisting(null)} busy={busy}
      onDifferent={(m, reason) => sendRequest({ possible_duplicate: true, duplicate_reason: reason, dismissed_match_id: m.id })} />;
  }

  return (
    <form onSubmit={submit} data-testid="register-card-form" className="bg-[#111] border border-[#EF4444]/40 rounded-md p-4 sm:p-6 space-y-4">
      <div className="font-display text-base sm:text-xl break-words leading-tight">Carta nueva · un solo envío</div>
      <Steps />
      <div className="text-xs text-neutral-300 bg-[#0A0A0A] border border-[#262626] rounded p-3">
        Llena la carta y tu publicación de una vez. El admin la revisa con tus fotos y, al aprobarla, <b>se publica sola</b> con tu precio. Normalmente en menos de 12 h.
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label className="text-xs text-neutral-400">Nombre *</Label><Input data-testid="register-card-name" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} required className="bg-[#171717] border-[#262626]" /></div>
        <div><Label className="text-xs text-neutral-400">Set / Edición *</Label><Input data-testid="register-card-set" value={form.set_name} onChange={(e) => setForm(f => ({ ...f, set_name: e.target.value }))} required className="bg-[#171717] border-[#262626]" /></div>
        <div><Label className="text-xs text-neutral-400">Serie (opcional)</Label><Input data-testid="register-card-serie" value={form.serie_name} onChange={(e) => setForm(f => ({ ...f, serie_name: e.target.value }))} placeholder={placeholders.serie} className="bg-[#171717] border-[#262626]" /></div>
        <div><Label className="text-xs text-neutral-400">Número de colección</Label><Input data-testid="register-card-number" value={form.number} onChange={(e) => setForm(f => ({ ...f, number: e.target.value }))} placeholder={placeholders.number} className="bg-[#171717] border-[#262626]" /></div>
        <div>
          <Label className="text-xs text-neutral-400">Idioma *</Label>
          <Select value={form.language} onValueChange={(v) => setForm(f => ({ ...f, language: v }))}>
            <SelectTrigger data-testid="register-card-language" className="bg-[#171717] border-[#262626]"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="es">Español</SelectItem><SelectItem value="en">Inglés</SelectItem><SelectItem value="ja">Japonés</SelectItem><SelectItem value="otro">Otro</SelectItem></SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-neutral-400">Variante *</Label>
          <Select value={form.variant} onValueChange={(v) => setForm(f => ({ ...f, variant: v }))}>
            <SelectTrigger data-testid="register-card-variant" className="bg-[#171717] border-[#262626]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {variantOptions.map((v) => (
                <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {/* Feb 2026 — solo aparece cuando variante=patron (Pokémon). El texto
          describe qué patrón trae la carta (Pokébola, Masterbola, patrón
          lucha, etc.) y se muestra al lado del badge "PATRÓN" en el catálogo. */}
      {!isRiftbound && form.variant === "patron" && (
        <div>
          <Label className="text-xs text-neutral-400">¿Qué patrón? (opcional)</Label>
          <Input
            data-testid="register-card-pattern-label"
            value={form.pattern_label}
            onChange={(e) => setForm(f => ({ ...f, pattern_label: e.target.value.slice(0, 60) }))}
            placeholder="Ej. Pokébola, Masterbola, patrón lucha…"
            className="bg-[#171717] border-[#262626]"
          />
          <div className="text-[11px] text-neutral-500 mt-1">Se muestra junto al badge de la carta en el catálogo. Puedes dejarlo vacío.</div>
        </div>
      )}
      <div className="space-y-3 p-3 rounded-[14px] border border-[#262626] bg-[#0A0A0A]" data-testid="p5-publication">
        <div className="text-[10px] uppercase tracking-wider text-neutral-500">Tu publicación</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs text-neutral-400">Condición *</Label>
            <Select value={draft.condition} onValueChange={(v) => setDraft((d) => ({ ...d, condition: v }))}>
              <SelectTrigger data-testid="p5-condition" className="bg-[#171717] border-[#262626]"><SelectValue /></SelectTrigger>
              <SelectContent>{DRAFT_CONDS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs text-neutral-400">Precio USD *</Label><Input data-testid="p5-price" type="number" min="0.25" step="0.01" value={draft.price} onChange={(e) => setDraft((d) => ({ ...d, price: e.target.value }))} className="bg-[#171717] border-[#FF4438]/60" /></div>
          <div>
            <Label className="text-xs text-neutral-400">Cantidad</Label>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" className="h-10 w-10 border-[#262626]" onClick={() => setDraft((d) => ({ ...d, quantity: Math.max(1, d.quantity - 1) }))} data-testid="p5-qty-minus">−</Button>
              <span className="w-8 text-center" data-testid="p5-qty">{draft.quantity}</span>
              <Button type="button" variant="outline" className="h-10 w-10 border-[#262626]" onClick={() => setDraft((d) => ({ ...d, quantity: d.quantity + 1 }))} data-testid="p5-qty-plus">+</Button>
            </div>
          </div>
        </div>
        {draft.quantity > 1 && (
          <label className="flex items-start gap-2 text-[11px] text-amber-200"><input type="checkbox" checked={draft.bulk} onChange={(e) => setDraft((d) => ({ ...d, bulk: e.target.checked }))} data-testid="p5-bulk" className="mt-0.5 accent-[#EF4444]" />Todas las unidades están en la misma condición.</label>
        )}
        {parseFloat(draft.price) > 0 && <SellerNetCards price={parseFloat(draft.price)} quantity={draft.quantity}
          ship={hasShipRates(shippingConfig) ? shipRange(shippingConfig) : null} onConfigureShip={onConfigureShip} />}
      </div>
      <div>
        <Label className="text-xs text-neutral-400">Notas para el admin (opcional)</Label>
        <Textarea data-testid="register-card-notes" value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Cualquier detalle útil que ayude a identificarla" className="bg-[#171717] border-[#262626]" />
      </div>
      <div>
        <Label className="text-xs text-neutral-400">Fotos reales de tu copia * (mínimo 2: frente y dorso)</Label>
        <div className="text-[11px] text-neutral-500 mb-2">
          Sirven para la revisión y son las fotos de tu publicación. Sin filtros ni sombras fuertes.
        </div>
        <MultiPhotoInput photos={photos} onChange={setPhotos} min={2} max={10} testid="register-card-photos" />
      </div>
      <div className="flex gap-2 flex-wrap">
        <Button data-testid="register-card-submit" type="submit" disabled={busy} className="bg-[#EF4444] hover:bg-[#DC2626]">
          {busy ? (
            <span className="inline-flex items-center gap-2"><HitRing tone="light" size={16} />{progress || "Enviando…"}</span>
          ) : "Enviar · se publica al aprobarse"}
        </Button>
        {onCancel && <Button type="button" variant="ghost" onClick={onCancel}>Cancelar</Button>}
      </div>
    </form>
  );
}

export default CustomCardForm;
